import type { ProBuilderMode } from '~/hooks/queries/use-pro-builder';

/**
 * Every on-screen sentence of Pro Builder (FRONT-3003), in one place so the
 * window, the Settings section and the admin card cannot drift apart.
 */
export const PRO_BUILDER_TITLE = 'Pro Builder';
export const PRO_BUILDER_POWERED_BY = 'Runs Claude Code';
export const PRO_BUILDER_BILLING_LINE =
  'Runs Claude Code. Model usage is billed to the credential you connect, not to EDDY.';
export const PRO_BUILDER_ORG_OFF_MEMBER =
  'Pro Builder is off for your organization. Ask an admin to turn it on.';
export const PRO_BUILDER_ORG_OFF_ADMIN_LEAD = 'Pro Builder is off for your organization.';
export const PRO_BUILDER_ORG_OFF_ADMIN_LINK = 'Turn it on in System Settings.';
export const PRO_BUILDER_ADMIN_SWITCH_LABEL = 'Pro Builder';
export const PRO_BUILDER_ADMIN_SWITCH_HELP =
  "Lets people in your organization run their own Claude Code in their EDDY workspace. Model usage is billed to each person's own credential.";
export const PRO_BUILDER_CHOICE_HEADING = 'How Claude Code signs in';
export const PRO_BUILDER_RESTART_NOTICE = 'The new connection applies when you restart the terminal.';
export const PRO_BUILDER_RESTARTED_NOTICE = 'Restarted — open Pro Builder to see it.';
export const PRO_BUILDER_TERMINAL_ENDED = 'The terminal session ended.';
export const PRO_BUILDER_TERMINAL_FAILED = "Terminal isn't available right now.";
export const PRO_BUILDER_TERMINAL_RECONNECTING = 'Reconnecting…';

export const PRO_BUILDER_MODE_LABELS: Record<ProBuilderMode, string> = {
  subscription: 'My Claude subscription — sign in inside the terminal',
  api_key: 'My own Anthropic API key',
  bedrock: "My company's AWS Bedrock account",
};

export const PRO_BUILDER_MODES: readonly ProBuilderMode[] = ['subscription', 'api_key', 'bedrock'];

/** The "Connected: …" sentence for the person's current mode. */
export function proBuilderConnectedLine(mode: ProBuilderMode, bedrockRegion: string | null): string {
  switch (mode) {
    case 'subscription':
      return 'Connected: your Claude subscription — sign in inside the terminal. Your code goes to Anthropic under your own Claude plan.';
    case 'api_key':
      return "Connected: your own Anthropic API key. Your code goes to Anthropic under that key's account.";
    case 'bedrock': {
      const region = bedrockRegion ?? 'your chosen region';
      return `Connected: your AWS Bedrock account (${region}). Your code goes to AWS Bedrock in ${region} under your company's AWS contract.`;
    }
  }
}

/** FRONT-3078: the Upload doorway's words. */
export const UPLOAD_BUTTON_TOOLTIP = 'Upload files for this app into its Uploaded Documents folder';
export const UPLOAD_DIALOG_TITLE = 'Upload documents';
export const UPLOAD_DIALOG_DESCRIPTION =
  "Files go into this app's Uploaded Documents folder. Claude Code knows to look there, and they are never published.";
export const UPLOAD_DROP_OVERLAY_TEXT = 'Drop to upload into Uploaded Documents';
export const UPLOAD_GENERIC_FAILURE = 'The upload failed. Try again.';
export const UPLOAD_INTERRUPTED = 'The upload was interrupted. Try again.';
export const UPLOAD_MAX_BYTES = 26_214_400;
export function uploadTooLargeSentence(name: string): string {
  return `“${name}” is larger than 25 MB, the most EDDY can upload at once.`;
}

/** Shown when the browser refuses a copy from the terminal — "c to copy" or Claude Code's mouse selection (FRONT-3172): the clipboard still holds whatever was there before. ⌘C copies the remembered selection inside a real copy event, which the browser allows. */
export const PRO_BUILDER_COPY_REFUSED = "Your browser didn't allow the copy. Press ⌘C (Ctrl+C) to copy what you selected, or click a link to open it.";

/** FRONT-3108: the consent card a Claude Code terminal waits on, and its task-list row. */
export const PRO_BUILDER_CONSENT_TITLE = 'Claude Code asks for your OK';
export const PRO_BUILDER_CONSENT_WAIT_NOTE = 'Claude Code is paused until you answer.';
export const PRO_BUILDER_CONSENT_TIME_TOOLTIP = 'Time left to answer. When it runs out, nothing is changed.';
export const PRO_BUILDER_CONSENT_APPROVE = 'Approve';
export const PRO_BUILDER_CONSENT_DECLINE = 'Decline';
export const PRO_BUILDER_CONSENT_APPROVING = 'Approving…';
export const PRO_BUILDER_CONSENT_DECLINING = 'Declining…';
/** The countdown pill's words, e.g. "9:42 left". */
export function proBuilderConsentTimeLeft(clock: string): string {
  return `${clock} left`;
}
export const PRO_BUILDER_CONSENT_SEND_FAILED = "Couldn't send your answer. Try again.";
export const PRO_BUILDER_NEEDS_OK = 'Needs your OK';

/** The window header's "Copy last answer" control and its status line. */
export const PRO_BUILDER_COPY_ANSWER_LABEL = 'Copy last answer';
export const PRO_BUILDER_COPY_ANSWER_TOOLTIP = "Copy Claude's latest answer, including the parts scrolled out of view";
export const PRO_BUILDER_COPY_ANSWER_COPIED = 'Copied';
export const PRO_BUILDER_COPY_ANSWER_NONE = "Claude Code hasn't finished an answer yet.";
export const PRO_BUILDER_COPY_ANSWER_TOO_LONG = 'That answer is too long to copy here. Select the part you need with the mouse instead.';
export const PRO_BUILDER_COPY_ANSWER_FAILED = "Couldn't copy the answer. Try again.";

/** FRONT-3228: the header's Claude Code settings popover (auto-compact threshold). */
export const PRO_BUILDER_SETTINGS_LABEL = 'Claude Code settings';
export const PRO_BUILDER_AUTO_COMPACT_SLIDER_LABEL = 'Auto-compact threshold';
export const PRO_BUILDER_AUTO_COMPACT_LEFT = '50%';
export const PRO_BUILDER_AUTO_COMPACT_RIGHT = 'Default';
export const PRO_BUILDER_AUTO_COMPACT_RESTART_NOTE = 'Applies after a restart.';
export const PRO_BUILDER_AUTO_COMPACT_RESTART_BUTTON = 'Restart Claude Code';
export const PRO_BUILDER_AUTO_COMPACT_LOAD_FAILED = "Couldn't load this setting.";
export const PRO_BUILDER_AUTO_COMPACT_LOADING = 'Loading this setting…';
export const PRO_BUILDER_AUTO_COMPACT_SAVE_FAILED = "Couldn't save the compact setting. Try again.";
